import React from 'react';
import PropTypes from 'prop-types';
import { VelocityTransitionGroup } from 'velocity-react';
import 'velocity-animate/velocity.ui';
import classNames from 'classnames';
import IPropTypes from 'react-immutable-proptypes';
import dateFormat from 'dateformat';
import { is } from 'immutable';

import tokenLarge from 'imported-assets/img/tokens/token_awarded_large.png';
import backgroundRadial from 'imported-assets/img/tokens/background_radial.png';
import {
    shouldKeyTrigger,
    createKeyboardFocusRestrictor,
    removeKeyboardFocusRestrictor,
} from 'phoenix2/utils/keyboard';
import { addKeyboardShortcuts, removeKeyboardShortcuts } from 'phoenix2/components/keyboard';
import { UnstartedIcon, InProgressIcon } from 'phoenix2/components/widgets/rewards-icons';

import { TeacherOverrideAlert } from './teacher-override';
import { RememberPasswordAlert } from './remember-password';
import { VideoAlert } from './video';
import { TrainingVideo } from './training-video/training-video';
import { TrainingFeedbackDialog } from './training-feedback/training-feedback-dialog';
import BWCheckIllustration from 'imported-assets/img/bw-check-illustration.svg';

const images = require.context('../../../imported-assets/img');

const alertTitle = {};

const defaultTitle = title => <div className="alert-title">{title}</div>;

alertTitle.debugInfo = title => [
    <div key="debug_title" className="alert-title debug-title">
        {title}
    </div>,
];

// Displays the title with 'New' to the left of it
alertTitle.newFeature = title => [
    <div key="new_feature_title" className="alert-title">
        <div className="alert-new-feature-new">New</div>
        {title}
    </div>,
];

const alertContent = {};

const defaultContent = ({ message, image, staticAssetsURL }) => [
    image ? (
        <div className="alert-image" key="image">
            <img src={`${staticAssetsURL}/${image}`} />
        </div>
    ) : null,
    message ? (
        <div className="alert-message" key="message">
            {message}
        </div>
    ) : null,
];

// Content for new feature alerts
alertContent.newFeature = ({ message }) => [
    <div className="alert-message" key="message">
        {message.get('messageElement').toJS()}
    </div>,
];

// Content for the alert shown when a student is awarded XP
alertContent.reward = ({ message }) => [
    message
        ? [
              <div key="award-pop-up-bold" className="alert-award-xp-message">
                  <div className="alert-award-background-size-setter">
                      <img className="alert-award-spinner" src={images('./radial-grad.png')} />
                      <img
                          className="alert-award-invisible-background"
                          src={images('./radial-grad.png')}
                      />
                      <div className="alert-award-inner-xp-message">
                          +{message.get('xpValue')}
                          <span className="alert-award-xp-unit"> XP</span>
                          <img
                              className={classNames('alert-image', 'alert-award-banner')}
                              src={images('./well-done-banner.png')}
                          />
                      </div>
                  </div>
                  <div className="alert-award-secondary-message" key="personalised-message">
                      {message.get('personalisedRemark')}
                      <br />
                      {"You've earned "}
                      {message.get('xpValue')} XP for completing this task.
                  </div>
              </div>,
          ]
        : null,
];

// Content for the alert shown in ground when announcing new achievements

alertContent.goalAchieved = ({ message, image }) => [
    image ? (
        <div className="alert-image" key="image">
            <img src={images('./' + image + '.png')} />
        </div>
    ) : null,
    <div className="alert-goal-message" key="message">
        <div>{message.get('achievementMessage')}</div>
        <div>Achieved on {dateFormat(message.get('achievementDate'), 'dd/mm/yyyy')}</div>
    </div>,
];

// Content for the alert shown when a student obtains a new Sparx Level
alertContent.onSparxLevelAchieved = ({ message }) => [
    <div className="alert-message" key="message">
        {message.get('messageElement').toJS()}
    </div>,
    <div className="alert-award-xp-message" key="alert-banner">
        <div className="alert-award-background-size-setter">
            <img className="alert-award-spinner" src={images('./radial-grad.png')} />
            <img
                className="alert-sparx-level-banner-overlay-image"
                key="banner-image"
                src={images('./banner_level_background_without_radial.png')}
            />
            <div className="alert-sparx-level-banner-overlay-text" key="text">
                {message.get('levelNumber')}
            </div>
            <img className="alert-award-invisible-background" src={images('./radial-grad.png')} />
        </div>
    </div>,
];

// Content for the alert shown when a student clicks on a complete level icon
// in the rewards page Sparx Level Progress section.
alertContent.rewardLevelComplete = ({ message }) => [
    <div className="alert-message" key="message">
        {message.get('messageElement').toJS()}
    </div>,
    <div
        className="alert-reward-complete-image"
        key="image"
        style={{
            backgroundImage: `url(${images('./banner_level_background_radial.png')})`,
        }}
    >
        {message.get('levelNumber')}
    </div>,
];

// Content for the alert shown when a student clicks on in progress level
// icon in the rewards page Sparx Level Progress section.
alertContent.rewardLevelInProgress = ({ message }) => [
    <div className="alert-message" key="message">
        {message.get('messageElement').toJS()}
    </div>,
    <div className="alert-reward-icon" key="icon">
        <InProgressIcon percentComplete={message.get('percentComplete')} />
    </div>,
];

// Content for the alert shown when a student clicks on an unstarted level
// icon in the rewards page Sparx Level Progress section.
alertContent.rewardLevelUnstarted = ({ message }) => [
    <div className="alert-message" key="message">
        {message.get('messageElement').toJS()}
    </div>,
    <div className="alert-reward-icon" key="icon">
        <UnstartedIcon />
    </div>,
];

// Content for the alert shown when a student presses a UI element in the
// Primary Times Tables screen
alertContent.primaryUIExplanation = ({ message }) => [
    <div className="alert-message" key="message">
        {message.get('messageElement').toJS()}
    </div>,
];

alertContent.debugInfo = ({ message }) => {
    const listItems = [];
    message.get('items').forEach((debugItem, index) => {
        const value = debugItem.get('value');
        if (value) {
            listItems.push(
                <div key={index + 't'}>
                    <strong>{debugItem.get('title')}:</strong>{' '}
                    <span className={'debug-' + debugItem.get('title').toLowerCase()}>{value}</span>
                </div>,
            );
        }
    });
    return [
        <div key="debug" className="alert-message debug-message">
            {listItems.length > 0 ? listItems : 'No question info to show'}
        </div>,
    ];
};

// Content for the alert shown when a teacher needs to override default app
// behaviour by entering a 4-digit pin (e.g. student locked into a task
// during a lesson
alertContent.teacherOverride = (props, onEndAlert) => [
    <TeacherOverrideAlert key="teacher-override" onEndAlert={onEndAlert} />,
];

// Content for the alert shown in the New User app, when the user confirms the
// new password they have just been shown
alertContent.rememberPassword = ({ message }, onEndAlert) => [
    <RememberPasswordAlert
        key="remember-password"
        onEndAlert={onEndAlert}
        newPassword={message.get('newPassword')}
        username={message.get('username')}
    />,
];

// Content for the video alert, to show a video for a question
alertContent.video = ({ message }, onEndAlert) => [
    <VideoAlert
        key={'video'}
        onEndAlert={onEndAlert}
        videoId={message.get('videoId')}
        skillId={message.get('skillId')}
        activityIndex={message.get('activityIndex')}
        skipToFinalFrame={message.get('skipToFinalFrame')}
        videoType={message.get('videoType')}
        disableSeek={message.get('disableSeek')}
        disableSkipButton={message.get('disableSkipButton')}
        corsSafe={message.get('corsSafe', false)}
        showSubtitles={message.get('showSubtitles')}
        buttonText={message.get('buttonText')}
        instructionText={message.get('instructionText')}
        onReachedVideoEnd={message.get('onReachedVideoEnd')}
        useSecondaryButton={message.get('useSecondaryButton')}
        autoPlay={message.get('autoPlay')}
        outroLength={message.get('outroLength')}
    />,
];

/**
 * Content for the alert box that appears when instructing a user to rotate the
 * screen in the skill delivery view if content has overflowed horizontally
 * @param message alert message
 */
alertContent.rotateScreen = ({ message }) => [
    message.get('title') ? (
        <div className={classNames('alert-title', 'alert-rotate-screen-title')} key="title">
            {message.get('title')}
        </div>
    ) : null,
    <div className={classNames('alert-image', 'alert-rotate-screen-image')} key="image">
        <img src={images('./rotate_device.png')} />
    </div>,
    message.get('message') ? (
        <div className="alert-rotate-screen-message" key="message">
            {message.get('message')}
        </div>
    ) : null,
];

// TODO: feature_flag: primaryTables
alertContent.primaryTablesTargets = ({ message }) => {
    const targets = message.get('targets', []);
    if (targets.count() > 0) {
        return [
            <div key="message">The most important questions for me to practise are:</div>,
            <div className="primary-targets" key="targets">
                {targets.map((target, i) => (
                    <div key={i}>{target}</div>
                ))}
            </div>,
        ];
    }
    return [
        <div className="primary-targets" key="message">
            Your targets will be shown here once you&apos;ve completed your initial 100 Club
            assessment.
        </div>,
    ];
};

// TODO: feature_flag: primaryTables
alertContent.primaryOnboardingFirst = () => [
    <div className="primary-onboarding-notification" key="message">
        <p>You will be using Sparx to help improve your times tables.</p>
        <p>
            You will get questions that are aimed at your level.
            <br />
            It is important that you <strong>answer the questions yourself</strong> and{' '}
            <strong>do your best to answer each question correctly.</strong>
        </p>
    </div>,
];

// TODO: feature_flag: primaryTables
alertContent.primaryOnboardingExtra = () => [
    <div className="primary-onboarding-notification" key="message">
        <p>
            When you answer questions in Sparx, we learn what you know and what you don&apos;t. If
            somebody else answers your questions for you we might give you questions that are too
            hard in future.
        </p>
        <p>
            The same could happen if you enter random answers. Try your best to answer each question
            correctly.
        </p>
    </div>,
];
alertTitle.primaryOnboardingExtra = () => [
    <div key="title" className="alert-title primary-onboarding-title">
        <strong>Remember:</strong> Only you should do your
        <br />
        Sparx times tables
    </div>,
];

// TODO: feature_flag: primaryTables
alertContent.tokenAlert = () => [
    <div className="token-img" key="token-img">
        <img src={tokenLarge} className="token-img-token" />
        <img src={backgroundRadial} className="token-img-bg" />
    </div>,
];

alertContent.innerComponent = ({ message }, endAlert) => [
    <React.Fragment key="content">
        {typeof message === 'function' ? message(endAlert) : message}
    </React.Fragment>,
];

alertContent.homeworkCancelled = () => [
    <div className="alert-message" key="message">
        <p>This homework has been cancelled by your teacher so is no longer compulsory.</p>
        <p>As you have started the homework already you will keep the XP you have gained.</p>
        <p>
            You are still able to complete your XP Boost and Target homework if you would like to.
        </p>
    </div>,
];

alertContent.trainingModeVideo = ({ message }, onEndAlert) => [
    <TrainingVideo
        key="training-video"
        videoId={message.get('videoId')}
        onReachedEnd={message.get('onReachedVideoEnd')}
        onEndAlert={onEndAlert}
        isStepComplete={message.get('isStepComplete')}
        context={message.get('context')}
        instructionText={message.get('instructionText')}
        onNextStep={message.get('onNextStep')}
    />,
];

alertContent.feedbackAlert = ({ message }, onEndAlert) => [
    <TrainingFeedbackDialog
        key="training-feedback-dialog"
        onSubmitFeedbackComment={message.get('onSubmitFeedbackComment')}
        onEndAlert={onEndAlert}
    />,
];

alertContent.trainingBookworkChecks = () => [
    <img src={BWCheckIllustration} key="training-bookwork-checks-image" />,
    <p key="training-bookwork-checks-text" style={{ textAlign: 'center', margin: 0 }}>
        <strong>Remember</strong> - you will need a <strong>pen and paper</strong> to write down
        your workings, as your students will, to complete your training.
    </p>,
];

alertContent.leaveFastTrackQuiz = () => [
    <div key="leave-fast-track-quiz" className="alert-message">
        <p style={{ textAlign: 'center', margin: 0 }}>
            Are you sure you want to exit this Fast Track Quiz?
        </p>
        <p style={{ textAlign: 'center', margin: 0 }}>
            Any answers you haven’t submitted will be lost.
        </p>
    </div>,
];

export class AlertContent extends React.Component {
    static displayName = 'AlertContent';

    render() {
        // Throws a component error in the client unit tests.
        // It does nothing in production.
        if (process.env.NODE_ENV === 'test' && this.props.type === 'test_error') {
            throw Error('This is a test error in a component');
        }

        const { title, type, buttons, noDefaultButton, endAlert, className } = this.props;
        const titleComponent = (alertTitle[type] || defaultTitle)(title);
        const content = (alertContent[type] || defaultContent)(this.props, endAlert);
        const focus = x => x && x.focus();

        let buttonsSection = null;

        // Generate buttons. If buttons have been provided in the props,
        // create those. Otherwise, create a default continue button unless
        // the noDefaultButton prop is true (i.e. the content will then have
        // its own way of dismissing the alert)
        if (buttons) {
            buttonsSection = buttons.map((button, i) => {
                const primaryArrow = button.get('primary') ? (
                    <span key="primary">
                        <i className="button-icon-right fas fa-angle-right" />
                    </span>
                ) : null;

                const buttonClass = classNames('alert-button', 'btn', {
                    'btn-secondary': button.get('secondary'),
                });

                return (
                    <div
                        tabIndex="0"
                        ref={i === 0 ? focus : null}
                        key={button.get('key')}
                        className={buttonClass}
                        onClick={() => this.click(button)}
                        onKeyDown={evt => shouldKeyTrigger(evt) && this.click(button)}
                        onMouseDown={evt => {
                            // Prevent focussing when clicked:
                            evt.preventDefault();
                        }}
                    >
                        {button.get('text')}
                        {primaryArrow && ' '}
                        {primaryArrow}
                    </div>
                );
            });
        } else if (!noDefaultButton) {
            const buttonClass = classNames('alert-button', 'btn');

            const primaryArrow = <i className="button-icon-right fas fa-angle-right" />;

            buttonsSection = (
                <div
                    className={buttonClass}
                    tabIndex="0"
                    ref={focus}
                    onMouseDown={evt => {
                        // Prevent focussing when clicked:
                        evt.preventDefault();
                    }}
                >
                    Continue
                    {primaryArrow && ' '}
                    {primaryArrow}
                </div>
            );
        }

        const alertButtons = buttonsSection && (
            <div className="alert-buttons">{buttonsSection}</div>
        );

        // If there is a default secondary action, create the cross button in
        // the top-right
        const secondaryAction = this._getSecondaryAction();

        const dismissButton = secondaryAction ? (
            <img
                className="alert-dismiss-button"
                src={images('./close_popover.png')}
                onClick={secondaryAction}
            />
        ) : null;

        // Class for alert box - append custom class if provided in alert spec:
        const alertBoxClassName = classNames('alert-box', className);

        // The alert box gas tabIndex=-1 so the keyboard focus can be set to it
        // when the alert opens programatically, ready to tab into the buttons,
        // but without the user being able to focus it
        return (
            <div className={alertBoxClassName} ref={el => (this._alertBox = el)}>
                <div
                    className="keyboard-focus-start-point"
                    tabIndex="-1"
                    ref={el => (this._keyboardFocusStartPoint = el)}
                />
                {dismissButton}
                {title ? titleComponent : null}
                <div className="alert-box-content">{content}</div>
                {alertButtons}
            </div>
        );
    }

    click(button) {
        this.props.endAlert(button.get('key'));
    }

    componentDidMount() {
        this._configureKeyboardShortcuts();

        // Restrict keyboard focus to within the alert
        this._focusRestrictor = createKeyboardFocusRestrictor(
            this._alertBox,
            this._keyboardFocusStartPoint,
            'alert',
        );

        // When the alert content appears, set keyboard focus to just before it
        this._keyboardFocusStartPoint.focus();
    }

    componentDidUpdate() {
        // If the alert content changes, reset keyboard focus
        this._keyboardFocusStartPoint.focus();
    }

    componentWillUnmount() {
        if (this._keyboardShortcutsId !== null) {
            removeKeyboardShortcuts(this._keyboardShortcutsId, this.props.keyboardHandlerId);
        }

        // Remove focus restrictor:
        removeKeyboardFocusRestrictor(this._focusRestrictor);

        // Fire event once keyboard focus has been unrestricted, so parent
        // can set focus to something sensible
        this.props.onClosed && this.props.onClosed();
    }

    _configureKeyboardShortcuts() {
        const { buttons, keyboardHandlerId } = this.props;

        const shortcuts = {};

        let shortcutAdded = false;

        // Add Escape shortcut to default secondary action, and Enter/Space to
        // default primary action:
        const secondaryAction = this._getSecondaryAction();
        if (secondaryAction) {
            shortcuts['Escape'] = secondaryAction;
            shortcutAdded = true;
        }

        const primaryAction = this._getPrimaryAction();
        if (primaryAction) {
            shortcuts[' '] = primaryAction;
            shortcuts['Enter'] = primaryAction;
            shortcutAdded = true;
        }

        if (buttons) {
            for (const button of buttons) {
                const btnShortcuts = button.get('shortcuts');
                if (btnShortcuts) {
                    for (const shortcut of btnShortcuts) {
                        shortcuts[shortcut] = () => {
                            this.click(button);
                            return true;
                        };
                        shortcutAdded = true;
                    }
                }
            }
        }

        if (shortcutAdded) {
            // Add shortcuts to handler
            this._keyboardShortcutsId = addKeyboardShortcuts(shortcuts, 'alert', keyboardHandlerId);
        } else {
            this._keyboardShortcutsId = null;
        }
    }

    /**
     * Gets the function for the alert's default primary action
     * (i.e. what the Space and Enter keys will do). The following rules
     * apply:
     * - If noDefaultButton = true, return null (a custom alert handles the
     * action)
     * - If there is only one button, return that button's action
     * - If there is more than button, return the button which has
     * primary=true, or null if none does
     * @private
     */
    _getPrimaryAction() {
        const { buttons, noDefaultButton, endAlert } = this.props;

        if (noDefaultButton) {
            return null;
        }

        // No buttons - i.e. there is a just a "Continue" button:
        if (!buttons || buttons.size === 0) {
            return () => endAlert();
        }

        // Only one button:
        if (buttons.size === 1) {
            return () => this.click(buttons.get(0));
        }

        // Find button with secondary = true
        const primaryButton = buttons.find(b => b.get('primary'));
        if (primaryButton) {
            return () => this.click(primaryButton);
        }

        // No primary action:
        return null;
    }

    /**
     * Gets the function for the alert's default secondary/dismiss action
     * (i.e. what the Escape key or the cross will do). The following rules
     * apply:
     * - If noDefaultButton = true, return null (a custom alert handles the
     * action)
     * - If there is only one button, return that button's action
     * - If there is more than button, return the button which has
     * secondary=true, or null if none does
     * @private
     */
    _getSecondaryAction() {
        const { buttons, noDefaultButton, endAlert, preventDismiss } = this.props;

        // If alert content handles its own buttons, just end the alert
        if (noDefaultButton) {
            if (preventDismiss) {
                return null;
            } else {
                return () => endAlert();
            }
        }

        // No buttons - i.e. there is a just a "Continue" button:
        if (!buttons || buttons.size === 0) {
            return () => endAlert();
        }

        // Only one button:
        if (buttons.size === 1) {
            return () => this.click(buttons.get(0));
        }

        // Find button with secondary = true
        const secondaryButton = buttons.find(b => b.get('secondary'));
        if (secondaryButton) {
            return () => this.click(secondaryButton);
        }

        // No secondary action:
        return null;
    }
}

AlertContent.propTypes = {
    title: PropTypes.string,
    type: PropTypes.string,
    buttons: IPropTypes.list,
    endAlert: PropTypes.func.isRequired,
    noDefaultButton: PropTypes.bool.isRequired,
    onClosed: PropTypes.func,
    keyboardHandlerId: PropTypes.string,
    className: PropTypes.string,
    preventDismiss: PropTypes.bool,
};

AlertContent.defaultProps = {
    noDefaultButton: false,
};

const ANIMATION_DURATION = 500;

const ALERT_ENTER_ANIMATION = {
    animation: 'transition.fadeIn',
    duration: ANIMATION_DURATION,
};

const ALERT_LEAVE_ANIMATION = {
    animation: 'transition.fadeOut',
    duration: ANIMATION_DURATION,
};

const ALERT_BOX_ENTER_ANIMATION = {
    animation: 'transition.expandIn',
    duration: ANIMATION_DURATION,

    // Makes the alert content container flex layout (by default, velocity
    // creates a block element)
    display: 'flex',
};

const ALERT_BOX_LEAVE_ANIMATION = {
    animation: 'transition.expandOut',
    duration: ANIMATION_DURATION,
};

class Alert extends React.Component {
    static propTypes = {
        alertSpec: IPropTypes.map,
        endAlert: PropTypes.func.isRequired,
        staticAssetsURL: PropTypes.string,
        onClosed: PropTypes.func,
        disableAnimations: PropTypes.bool,
        keyboardHandlerId: PropTypes.string,
        openAlert: PropTypes.func,
    };

    static displayName = 'Alert';

    constructor(props) {
        super(props);

        // This id is used to track when the alert content changes, so
        // velocity can animate alerts in and out
        this._alertId = 0;
    }

    UNSAFE_componentWillReceiveProps(nextProps) {
        // If the alert spec changes, we increment the id. The id is
        // used in the key for the alert content. Then, if one alert spec is
        // replaced by another, velocity knows that the alert has changed and
        // performs the expandOut-expandIn animation
        if (nextProps.alertSpec && !is(nextProps.alertSpec, this.props.alertSpec)) {
            this._alertId += 1;

            // Let analytics know a new alert has opened:
            if (nextProps.openAlert) {
                nextProps.openAlert();
            }
        }
    }

    render() {
        const {
            endAlert,
            staticAssetsURL,
            onClosed,
            alertSpec,
            disableAnimations,
            keyboardHandlerId,
        } = this.props;

        // Overlay - A full screen semi-transparent background
        const overlay = alertSpec && (
            <div className="alert-overlay-container" key={'alert-overlay-' + this._alertId}>
                <div className="alert-overlay" />
            </div>
        );

        // Content. The alert-box-container puts the alert in the centre of
        // the screen. The key changes when the alert spec changes so
        // velocity performs the expand-out, expand-in animation
        const alertContent = alertSpec && (
            <div className="alert-box-container" key={'alert-content-' + this._alertId}>
                <AlertContent
                    title={alertSpec.get('title')}
                    message={alertSpec.get('message')}
                    image={alertSpec.get('image')}
                    type={alertSpec.get('type')}
                    buttons={alertSpec.get('buttons')}
                    noDefaultButton={alertSpec.get('noDefaultButton')}
                    endAlert={endAlert}
                    staticAssetsURL={staticAssetsURL}
                    onClosed={onClosed}
                    keyboardHandlerId={keyboardHandlerId}
                    className={alertSpec.get('className')}
                    preventDismiss={alertSpec.get('preventDismiss')}
                />
            </div>
        );

        let overlayTransitionGroup = null;
        let contentTransitionGroup = null;

        if (!disableAnimations) {
            overlayTransitionGroup = (
                <VelocityTransitionGroup
                    className="alert-overlay-velocity-wrapper"
                    component="div"
                    enter={ALERT_ENTER_ANIMATION}
                    leave={ALERT_LEAVE_ANIMATION}
                    runOnMount={true}
                >
                    {overlay}
                </VelocityTransitionGroup>
            );

            contentTransitionGroup = (
                <VelocityTransitionGroup
                    className="alert-box-velocity-wrapper"
                    component="div"
                    enter={ALERT_BOX_ENTER_ANIMATION}
                    leave={ALERT_BOX_LEAVE_ANIMATION}
                    runOnMount={true}
                >
                    {alertContent}
                </VelocityTransitionGroup>
            );
        } else {
            overlayTransitionGroup = <div>{overlay}</div>;
            contentTransitionGroup = <div>{alertContent}</div>;
        }

        const alertClassnames = classNames('alert', {
            'alert-allow-fullscreen': alertSpec && alertSpec.get('allowFullscreen'),
        });

        return (
            <div className={alertClassnames} onClick={() => alertSpec && this.done()}>
                {overlayTransitionGroup}
                {contentTransitionGroup}
            </div>
        );
    }

    done() {
        const { alertSpec, endAlert } = this.props;

        if (!alertSpec.get('buttons') && !alertSpec.get('noDefaultButton')) {
            endAlert();
        }
    }
}

export default Alert;
